// src/pages/About.jsx
import React from "react";
import { Helmet } from "react-helmet-async";

export default function About() {
  const siteOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  const highlights = [
    "Who we are and our mission",
    "Our premium chauffeur services",
    "Why customers trust us",
    "How our service works",
    "Contact & booking options",
  ];

  const values = [
    "Reliability",
    "Punctuality",
    "Discretion",
    "Premium Quality",
    "24/7 Service",
    "Customer Focus",
  ];

  const faqItems = [
    {
      question: "What services does Fahrlux offer?",
      answer:
        "Chauffeur service, limousine service, airport transfers (Frankfurt FRA), business rides, and VIP transport across Germany.",
    },
    {
      question: "Is short-notice booking available?",
      answer:
        "Yes, depending on availability, short-notice bookings are possible.",
    },
    {
      question: "Which regions do you operate in?",
      answer:
        "Hanau, Frankfurt, Offenbach, the Rhine-Main region, and nationwide Germany.",
    },
  ];

  return (
    <div>
      {/* SEO */}
      <Helmet>
        <title>About Fahrlux | Premium Chauffeur Service Germany</title>
        <meta
          name="description"
          content="Fahrlux premium chauffeur & limousine service in Hanau & Rhine-Main. Luxury rides, business transfers & airport transportation in Germany."
        />
        <meta name="robots" content="index,follow" />
        {pageUrl && <link rel="canonical" href={pageUrl} />}
      </Helmet>

      {/* HERO */}
      <section
        className="text-white d-flex align-items-center"
        style={{
          minHeight: "90vh",
          backgroundImage:
            "linear-gradient(rgba(0,0,0,.70),rgba(0,0,0,.70)), url('/images/limousine-hero.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container text-center">
          <h1 className="display-3 fw-bold">
            Fahrlux – Chauffeur & Taxi Alternative{" "}
          </h1>
          <p className="lead mt-3">
            Premium Chauffeur & Limousine Service in Germany
          </p>
        </div>
      </section>

      {/* WHAT USERS LEARN */}
      <section className="container py-5 text-center">
        <h2>What You Will Learn About Us</h2>
        <p className="text-muted">
          This page gives you a complete overview of Fahrlux
        </p>

        <div className="row g-3 mt-3">
          {highlights.map((h) => (
            <div key={h} className="col-md-6 col-lg-4">
              <div className="p-3 border rounded-4 shadow-sm">✔ {h}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-light py-5">
        <div className="container row align-items-center g-5">
          <div className="col-lg-6">
            <img
              src="/images/fleet.jpg"
              alt="Fahrlux Fleet"
              className="img-fluid rounded-4 shadow"
            />
          </div>

          <div className="col-lg-6">
            <h2>Our Mission</h2>
            <p className="text-muted">
              Fahrlux was founded to redefine premium mobility in Germany. Our
              focus is comfort, safety, and professionalism for both business
              and private clients.
            </p>
          </div>
        </div>
      </section>

      {/* COMPANY PROFILE */}
      <section className="container py-5">
        <div className="row align-items-center g-5">
          <div className="col-lg-6">
            <h2>Company Profile</h2>
            <p className="text-muted">
              Fahrlux is a premium chauffeur service in Germany. We combine
              luxury vehicles, professional drivers, and modern booking systems.
            </p>
          </div>

          <div className="col-lg-6">
            <img
              src="/images/limousine-hero.jpeg"
              alt="Company"
              className="img-fluid rounded-4 shadow"
            />
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="container text-center py-5">
        <h2>Our Values</h2>

        <div className="row g-3 mt-3">
          {values.map((v) => (
            <div key={v} className="col-6 col-md-4 col-lg-2">
              <div className="p-3 border rounded-4 shadow-sm">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="bg-dark text-white py-5 text-center">
        <div className="container">
          <h2>How We Work</h2>

          <div className="row g-4 mt-3">
            {[
              "Request booking",
              "Vehicle & driver assigned",
              "Pickup",
              "Safe & comfortable ride",
            ].map((s) => (
              <div key={s} className="col-md-3">
                <div className="p-3 bg-white text-dark rounded-4">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MANAGEMENT */}
      <section className="bg-light py-5 text-center">
        <div className="container">
          <h2>Management</h2>
          <p className="text-muted mb-4">Leadership team behind Fahrlux</p>

          <div className="row g-4 justify-content-center">
            {/* Founder 1 */}
            <div className="col-md-5 col-lg-4">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <img
                  src="/images/founder.png"
                  alt="Fardin Azimi"
                  className="rounded-circle mb-3"
                  style={{
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/images/founder.png";
                  }}
                />
                <h4>Fardin Azimi</h4>
                <p className="text-muted">Founder & Managing Director</p>
                <p className="text-muted small">
                  Responsible for strategy, vision, and business development.
                </p>
              </div>
            </div>

            {/* Founder 2 */}
            <div className="col-md-5 col-lg-4">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <img
                  src="/images/founderw.png"
                  alt="Abdul Wahab Wojdan"
                  className="rounded-circle mb-3"
                  style={{
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/images/founder.png";
                  }}
                />
                <h4>Abdul Wahab Wojdan</h4>
                <p className="text-muted">Managing Director</p>
                <p className="text-muted small">
                  Responsible for operations and customer service quality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container py-5">
        <h2 className="text-center">Frequently Asked Questions</h2>

        <div className="mt-4">
          {faqItems.map((f, i) => (
            <details key={i} className="p-3 border rounded-4 mb-2">
              <summary className="fw-bold">{f.question}</summary>
              <p className="text-muted mt-2">{f.answer}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="text-center py-5">
        <h2>Ready for Premium Service?</h2>
        <a href="/contact" className="btn btn-dark btn-lg mt-3">
          Contact Us
        </a>
      </section>
    </div>
  );
}
